import { PageChrome } from "@/components/page-chrome"
import { ExternalLink, Home, Reply, UserPlus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useShowProps } from "./page-props"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { SheetPage, SheetPageTrigger } from "@/components/ui/sheet-page"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { WorkspaceLinkCards } from "@/components/workspace/link-cards"
import { WORKSPACE_OWNERS } from "@/components/workspace/definition"
import { useRouteWorkspace } from "@/components/workspace/page-chrome"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const {
    teacher,
    team_invitations,
    learner_relief_requests,
    educator_relief_requests,
  } = useShowProps()
  const workspace = useRouteWorkspace(WORKSPACE_OWNERS.teacher)
  const { t } = useTranslation("workspace")
  const { t: tNav } = useTranslation("nav")
  const { t: tRequestType } = useTranslation("school_relief_listings", {
    keyPrefix: "requests.types",
  })

  const invitationsLength =
    team_invitations.length +
    learner_relief_requests.length +
    educator_relief_requests.length

  return (
    <>
      <PageChrome
        icon={Home}
        title={tNav("teacher_workspace_title", { name: teacher.name })}
      />
      <PageBody role="region">
        {invitationsLength > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-start gap-6 align-bottom">
              <UserPlus className="fill-brand-wise-green size-8" />
              <CardTitle>
                {t("invitations_active", { count: invitationsLength })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul
                data-testid="invitation-list"
                className="grid grid-cols-3 gap-6"
              >
                {team_invitations.map((invitation) => (
                  <InvitationCard
                    key={invitation.id}
                    dialogId={`respond_to_invitation_${invitation.id}`}
                    itemUrl={invitation.team_profile_url}
                    itemUrlTitle={t("invitation.team_invitation.view_profile")}
                    responseUrl={`/educators/invitations/${invitation.id}/response/new`}
                  >
                    <CardTitle data-testid="team-name">
                      {invitation.team_profile_name}
                    </CardTitle>
                    <Badge>{t(`invitation.status.${invitation.state}`)}</Badge>
                  </InvitationCard>
                ))}
                {learner_relief_requests.map((request) => (
                  <InvitationCard
                    key={request.id}
                    dialogId={`respond_to_${request.type}_${request.id}`}
                    itemUrl={request.relief_listing_url}
                    itemUrlTitle={t(
                      "invitation.relief_listing_request.view_listing",
                    )}
                    responseUrl={request.response_url}
                  >
                    <CardTitle data-testid="school-name">
                      {t("invitation.relief_listing_request.position_title", {
                        title: request.relief_listing_title,
                        employer: request.school_name,
                      })}
                    </CardTitle>
                    <div className="flex flex-row flex-wrap gap-2">
                      <Badge>{tRequestType(request.type)}</Badge>
                      <Badge>{t(`invitation.status.${request.state}`)}</Badge>
                    </div>
                  </InvitationCard>
                ))}
                {educator_relief_requests.map((request) => (
                  <InvitationCard
                    key={request.id}
                    dialogId={`respond_to_${request.type}_${request.id}`}
                    itemUrl={request.relief_listing_url}
                    itemUrlTitle={t(
                      "invitation.relief_listing_request.view_listing",
                    )}
                    responseUrl={request.response_url}
                  >
                    <CardTitle data-testid="educator-relief-listing-name">
                      {t("invitation.relief_listing_request.position_title", {
                        title: request.relief_listing_title,
                        employer: request.educator_name,
                      })}
                    </CardTitle>
                    <div className="flex flex-row flex-wrap gap-2">
                      <Badge>{tRequestType(request.type)}</Badge>
                      <Badge>{t(`invitation.status.${request.state}`)}</Badge>
                    </div>
                  </InvitationCard>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent
            className="grid grid-cols-3 gap-6 py-6"
            aria-label="Actions"
            role="navigation"
          >
            <WorkspaceLinkCards workspace={workspace} />
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

function InvitationCard({
  itemUrl,
  itemUrlTitle,
  responseUrl,
  dialogId,
  children,
}: {
  itemUrl: string
  itemUrlTitle: string
  responseUrl: string
  dialogId: string
  children: React.ReactNode
}) {
  const { t } = useTranslation("workspace")

  return (
    <Card data-testid="invitation-item">
      <CardHeader>
        <div className="flex flex-col gap-1">{children}</div>
      </CardHeader>
      <CardFooter className="flex flex-row flex-wrap gap-4">
        <SheetPage href={responseUrl} dialogId={dialogId}>
          <SheetPageTrigger asChild>
            <Button>
              <Reply />
              {t("invitation.respond")}
            </Button>
          </SheetPageTrigger>
        </SheetPage>
        <Link href={itemUrl} as={Button}>
          <ExternalLink />
          {itemUrlTitle}
        </Link>
      </CardFooter>
    </Card>
  )
}
